import React, {useCallback, useMemo, useRef, useState} from 'react';

import DocumentFileRow from './DocumentFileRow';
import type {
  DocumentRequirement,
  DocumentUploadFile,
} from './documentDefinitions';

type DocumentUploadModalProps = {
  open: boolean;
  requirement: DocumentRequirement | null;
  files: DocumentUploadFile[];
  onClose: () => void;
  onUploadFiles: (files: File[]) => void;
  onRemoveFile: (file: DocumentUploadFile) => void;
  onDownloadFile: (file: DocumentUploadFile) => void;
  isUploading?: boolean;
};

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx'];

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  open,
  requirement,
  files,
  onClose,
  onUploadFiles,
  onRemoveFile,
  onDownloadFile,
  isUploading,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxSize = requirement?.maxFileSizeMb ?? 10;
  const allowMultiple = Boolean(requirement?.allowMultiple);

  const hasUploading = useMemo(
    () => files.some((file) => file.status === 'uploading'),
    [files],
  );

  const openPicker = () => inputRef.current?.click();

  const validateFiles = useCallback(
    (list: FileList | null) => {
      const maxBytes = maxSize * 1024 * 1024;
      const accepted = new Set(
        (requirement?.acceptedMimeTypes || []).concat([
          'image/jpeg',
          'image/png',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ]),
      );

      if (!list || list.length === 0) return [] as File[];

      const problems: string[] = [];
      let result: File[] = [];

      Array.from(list).forEach((file) => {
        if (file.size > maxBytes) {
          problems.push(`${file.name} exceeds ${maxSize}MB`);
        }

        if (!accepted.has(file.type)) {
          problems.push(`${file.name} is not a supported type`);
        }

        if (file.size <= maxBytes && accepted.has(file.type)) {
          result.push(file);
        }
      });

      if (!allowMultiple && result.length > 1) {
        problems.push('Only one file can be uploaded for this document');
        result = result.slice(0, 1);
      }

      setError(problems.length > 0 ? problems.join('. ') : null);
      return result;
    },
    [allowMultiple, maxSize, requirement?.acceptedMimeTypes],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = validateFiles(event.target.files);
    if (nextFiles.length > 0) onUploadFiles(nextFiles);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const nextFiles = validateFiles(event.dataTransfer.files);
      if (nextFiles.length > 0) onUploadFiles(nextFiles);
    },
    [onUploadFiles, validateFiles],
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  if (!open || !requirement) return null;

  const canClose = !hasUploading;
  const title = `${requirement.name}${
    requirement.isRequired ? '' : ' (optional)'
  }`;

  return (
    <div
      className="company-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Upload documents for ${requirement.name}`}
    >
      <div
        className="company-modal__backdrop"
        onClick={() => {
          if (canClose) onClose();
        }}
      />

      <div
        className="company-modal__panel document-upload-modal"
        aria-live="polite"
      >
        <header>
          <h3>{title}</h3>
          <button
            type="button"
            className="link-button"
            onClick={onClose}
            disabled={!canClose}
            aria-disabled={!canClose}
            aria-label="Close"
          >
            Close
          </button>
        </header>

        {requirement.helperText ? (
          <p className="company-modal__description">{requirement.helperText}</p>
        ) : null}

        <div
          className={`document-upload-dropzone${
            dragActive ? ' is-dragging' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={openPicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openPicker();
            }
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          aria-label={`Upload files. Max ${maxSize}MB. Supported: JPG, PNG, PDF, DOCX.`}
        >
          <div className="document-upload-dropzone__content">
            <strong>Drag files here</strong>
            <span>or click to select</span>
          </div>

          <input
            ref={inputRef}
            type="file"
            style={{display: 'none'}}
            multiple={allowMultiple}
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={handleInputChange}
          />
        </div>

        {error ? <div className="form-alert is-error">{error}</div> : null}

        <div className="document-upload-files">
          {files.length === 0 ? (
            <p className="form-note">No files added yet.</p>
          ) : (
            files.map((file) => (
              <DocumentFileRow
                key={file.uid}
                file={file}
                onDownload={onDownloadFile}
                onRemove={onRemoveFile}
              />
            ))
          )}
        </div>

        <footer className="company-modal__actions">
          <button
            type="button"
            className="money-btn money-btn--secondary money-btn--sm"
            onClick={onClose}
            disabled={!canClose}
          >
            Done
          </button>
          <button
            type="button"
            className="money-btn money-btn--primary money-btn--sm"
            onClick={openPicker}
            disabled={Boolean(isUploading || hasUploading)}
          >
            {hasUploading ? 'Uploading...' : '+ Add files'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
